import React, { useEffect, useState, useContext } from 'react'
import { HiUserGroup, HiUser, HiLink } from 'react-icons/hi'
import { FaCalendar, FaChartArea, FaClipboardCheck } from 'react-icons/fa'
import { IoHome, IoLogOut } from 'react-icons/io5'
import { FiChevronDown } from 'react-icons/fi'
import { Link, useNavigate } from 'react-router-dom'
import body from '../../helpers/body'
import storage from '../../helpers/storage'
import { can, Feature, Action, refreshPermissions } from '../../helpers/permissions'
import axiosInstance from '../../services/axios.service'
import EventContext from '../../context/events/eventContext'
import ResourceContext from '../../context/resource/resourceContext'

const Sidebar = () => {

  const eventContext = useContext(EventContext)
  const resourceContext = useContext(ResourceContext)

  useEffect(() => {
    body.changeBackground('bg-bck')
    eventContext.getEvents(10, 1)
    eventContext.getUserSession()
    eventContext.getSessions(eventContext.pagination && eventContext.pagination.limit !== undefined ? eventContext.pagination.limit : 10, eventContext.pagination && eventContext.pagination.page !== undefined ? eventContext.pagination.page : 1)
    eventContext.getStats()
    if (storage.getStatus() === active) {
      setActive(storage.getStatus())
    }

    setActive(storage.fetchLegacy('sb-menu') ? storage.fetchLegacy('sb-menu') : 'dashboard');

  }, [])

  const [active, setActive] = useState('')
  // Which group the reader has opened by hand, if any.
  const [expanded, setExpanded] = useState(null)

  const navigate = useNavigate()

  const [, setPermissionsVersion] = useState(0)

  useEffect(() => {
    // Corrects a session that signed in before grants existed, or whose
    // access changed while it was open.
    if (!storage.checkToken()) return
    refreshPermissions(
      axiosInstance,
      process.env.REACT_APP_BASE_URL,
      storage.getConfigWithBearer()
    ).then(() => setPermissionsVersion((v) => v + 1))
  }, [])

  const handleClick = (e, t, url, select) => {

    if (e) { e.preventDefault() }
    storage.keepLegacy('sb-menu', t)

    setActive(t)
    resourceContext.getSelect(active)
    storage.saveStatus(t)
    navigate(`/${url}`)


  }

  const logout = (e) => {
    if (e) e.preventDefault()
    storage.clearAuth()
    navigate('/')
  }

  /**
   * One list, each entry declaring the grant it needs.
   *
   * There were five near-identical copies of this — one per role — and five
   * role checks below choosing between them, so adding a screen meant
   * editing it in five places and remembering which roles should see it.
   * Now a screen names what it requires and the console shows whatever the
   * signed-in account can actually reach.
   */
  const routes = [
    { title: 'dashboard', url: 'dashboard', icon: <IoHome />, show: false },
    {
      title: 'events',
      url: 'dashboard/events',
      icon: <FaCalendar />,
      show: false,
      feature: Feature.EVENTS,
    },
    {
      title: 'participants',
      url: 'dashboard/participants',
      icon: <HiUserGroup />,
      show: true,
      feature: Feature.PARTICIPANTS,
    },
    // Accommodation, groups and sessions are one job — getting an event
    // ready — done in that order. Grouped under a heading with the setup
    // page first, so it reads as a sequence rather than four unrelated
    // screens an administrator has to know the order of.
    {
      section: 'Before check-in',
      // Accommodation, resource groups and sessions live inside this page
      // as tabs — one job, one destination, rather than three items an
      // administrator had to know the order of.
      title: 'event setup',
      url: 'dashboard/setup',
      icon: <FaClipboardCheck />,
      show: true,
      feature: Feature.SESSIONS,
    },
    {
      section: 'Reporting',
      title: 'reports',
      url: 'dashboard/reports',
      icon: <FaChartArea />,
      show: false,
      feature: Feature.PARTICIPANT_REPORTS,
      // Collapsed under Reports rather than listed one by one: six
      // near-identical entries read as six unrelated places.
      children: [
        {
          title: 'registrations',
          url: 'dashboard/reports/registrations',
          feature: Feature.PARTICIPANT_REPORTS,
        },
        {
          title: 'people',
          url: 'dashboard/reports/people',
          feature: Feature.PARTICIPANT_REPORTS,
        },
        {
          title: 'attendance',
          url: 'dashboard/reports/attendance-report',
          feature: Feature.PARTICIPANT_REPORTS,
        },
        {
          title: 'follow-ups',
          url: 'dashboard/reports/follow-ups',
          feature: Feature.PARTICIPANT_REPORTS,
        },
        {
          title: 'payments',
          url: 'dashboard/reports/transactions-report',
          feature: Feature.PARTICIPANT_REPORTS,
        },
        {
          title: 'centre reports',
          url: 'dashboard/reports/center',
          feature: Feature.CENTER_REPORTS,
        },
      ],
    },
    // { title: 'communications', url: 'dashboard/communications', icon: <BsChatLeftDots />, show: false },
    // Last, next to Logout: managing who may sign in is an account-level
    // concern rather than part of running an event.
    {
      section: 'Administration',
      title: 'admins',
      url: 'dashboard/users',
      icon: <HiUser />,
      show: false,
      feature: Feature.USERS,
    },
    // Attendee accounts, which are not the admins above them and had no
    // screen at all — so somebody locked out had to be found through a
    // registration, and an account with none could not be found.
    {
      title: 'attendee accounts',
      url: 'dashboard/users/accounts',
      icon: <HiUserGroup />,
      show: false,
      feature: Feature.USERS,
    },
    // tarm.cc. Its own grant: a link for an event and the click counts
    // across every link are different jobs, and the second is not part of
    // running an event.
    {
      title: 'short links',
      url: 'dashboard/short-links',
      icon: <HiLink />,
      show: false,
      feature: Feature.SHORT_LINKS,
    },
  ]

  // A route with no `feature` is open to anyone signed in.
  const visibleRoutes = routes.filter(
    (route) => !route.feature || can(route.feature, Action.VIEW)
  )

  // A `section` on a route opens a new group; the heading is dropped when
  // permissions have hidden everything that would have sat under it.
  const renderedLinks = visibleRoutes.map((route, i) => {
    // A parent's children are shown while any of them — or the parent
    // itself — is where you are, so the group opens to where you already
    // are rather than needing to be opened.
    const children = (route.children ?? []).filter(
      (child) => !child.feature || can(child.feature, Action.VIEW)
    )
    const onChild = children.some((child) => active === child.title)
    const open = expanded === route.title || active === route.title || onChild

    return (
      <div key={i}>
        {route.section && (
          <p className="px-5 pt-4 pb-2 text-[10px] font-semibold uppercase tracking-wider text-[#247ca5]/60">
            {route.section}
          </p>
        )}
        <div className="flex items-center">
          <Link
            onClick={(e) => handleClick(e, route.title, route.url, route.show)}
            to={``}
            className={`flex flex-1 items-center mb-4 px-5 py-2 rounded-lg ${active === route.title ? 'text-[#14a1db]' : ''}`}
          >
            <span className={`${active === route.title ? 'text-[#14a1db] font-semibold' : 'text-[#247ca5]'}  text-xl stroke-3`}>{route.icon}</span>
            <span className={`${active === route.title ? 'text-[#14a1db] font-medium' : 'text-[#247ca5]'} pl-7 text-sm capitalize`}>{route.title}</span>
          </Link>

          {children.length > 0 && (
            <button
              type="button"
              onClick={() => setExpanded(open ? null : route.title)}
              aria-label={`${open ? 'Collapse' : 'Expand'} ${route.title}`}
              aria-expanded={open}
              className="mb-4 mr-3 flex h-7 w-7 items-center justify-center rounded-md text-[#247ca5] hover:bg-white/70"
            >
              <FiChevronDown
                className={`transition-transform ${open ? '' : '-rotate-90'}`}
              />
            </button>
          )}
        </div>

        {children.length > 0 && open && (
          <div className="mb-4 ml-[3.1rem] flex flex-col gap-1 border-l border-[#247ca5]/20 pl-4">
            {children.map((child) => (
              <Link
                key={child.title}
                to={``}
                onClick={(e) => handleClick(e, child.title, child.url, child.show)}
                className={`py-1.5 text-sm capitalize ${
                  active === child.title
                    ? 'font-medium text-[#14a1db]'
                    : 'text-[#247ca5]'
                }`}
              >
                {child.title}
              </Link>
            ))}
          </div>
        )}
      </div>
    )
  })

  return (
    <>
      {/* A column: the logo and the sign-out stay put, only the links
          scroll. Sign out used to sit at the end of the scrolling list,
          below every nav item, in a container whose scrollbar is hidden —
          so on a short screen it was off the bottom with nothing to
          suggest there was anything down there. Adding one nav item was
          enough to lose it. */}
      <div className="fixed flex h-screen w-[260px] flex-col overflow-hidden bg-[#ecf5ff] z-50">

        <div className='flex shrink-0 items-center justify-center h-[65px] bg-[#d6f2ff]'>
          <img src="../../../images/assets/img@logo.png" width={60} alt="" />

        </div>

        <div className="min-h-0 flex-1 overflow-y-auto scrollbar-hidden px-4 pt-7">
          <div className='mb-12 text-center '>
            {/* Set as capitals in the text, not by a CSS transform — the
                name is a wordmark, and it should read the same wherever the
                string is copied to. */}
            <p className="text-[#3c4382] text-sm font-bold tracking-[0.18em]">
              CAMS
            </p>
            <p className="mt-0.5 text-[10px] uppercase tracking-[0.1em] text-[#3c4382]/70">
              Central Attendance &amp; Management System
            </p>
            <p className="pt-4 text-xs font-semibold capitalize text-[#247ca5]">
              {storage.getUser()}
            </p>
          </div>
          {renderedLinks}
        </div>

        <div className="shrink-0 border-t border-[#c9e3f5] px-4 py-3">
          <Link
            onClick={(e) => logout(e)}
            to={``}
            className="flex items-center rounded-lg px-5 py-2 hover:bg-[#d6f2ff]"
          >
            <span className="text-[#247ca5] font-semibold text-2xl stroke-2">
              <IoLogOut />
            </span>
            <span className="text-[#247ca5] font-medium pl-7 text-sm capitalize">
              Sign out
            </span>
          </Link>
        </div>
      </div>
    </>
  )
};

export default Sidebar;
